'use client';
import React, { ReactNode, useMemo } from 'react';
import { superPropertiesPlugin } from './plugins/super-properties';
import { appNamePlugin } from './plugins/app-name';
import { Analytics } from 'analytics';
import { AnalyticsProvider } from './analytics';
import segmentPlugin from '@analytics/segment';
import { CreateAnalyticsInstanceParams } from './types';

export const createAnalyticsInstance = ({
  appName,
  domains,
  segmentWriteKey,
  environment,
  enabled = true,
  superProperties,
}: CreateAnalyticsInstanceParams) => {
  return Analytics({
    app: appName,
    plugins: [
      segmentPlugin({
        writeKey: segmentWriteKey,
        options: {
          domains,
          crossDomainLinker: true,
        },
        enabled,
      }),
      appNamePlugin({
        appName,
      }),
      superPropertiesPlugin({
        initialProps: {
          environment: environment ?? 'development',
          ...superProperties,
        },
      }),
    ],
  });
};

interface ManagedAnalyticsProviderProps extends CreateAnalyticsInstanceParams {
  children: ReactNode;
}

/**
 * Managed Analytics Provider component that wraps children with a pre-configured analytics context.
 * This ensures consistent analytics configuration with Segment and plugins across the entire application.
 */
export const ManagedAnalyticsProvider: React.FC<
  ManagedAnalyticsProviderProps
> = ({
  children,
  appName,
  domains,
  segmentWriteKey,
  environment,
  enabled,
  superProperties,
}) => {
  const analytics = useMemo(
    () =>
      createAnalyticsInstance({
        appName,
        domains,
        segmentWriteKey,
        environment,
        enabled,
        superProperties,
      }),
    [appName, domains, segmentWriteKey, environment, superProperties],
  );
  return <AnalyticsProvider instance={analytics}>{children}</AnalyticsProvider>;
};

/**
 * Higher-order component that wraps a component with the ManagedAnalyticsProvider.
 * Usage: const WrappedComponent = withAnalytics(MyComponent, config);
 */
export const withAnalytics = <P extends object>(
  Component: React.ComponentType<P>,
  analyticsConfig: CreateAnalyticsInstanceParams,
) => {
  const WrappedComponent = (props: P) => (
    <ManagedAnalyticsProvider {...analyticsConfig}>
      <Component {...props} />
    </ManagedAnalyticsProvider>
  );

  WrappedComponent.displayName = `withAnalytics(${Component.displayName || Component.name})`;

  return WrappedComponent;
};
